/**
 * Wave 4 SEO cluster (part A): Spain long-tail — availability, signature,
 * type/color, LinkedIn URL, GitHub, TFG/TFM. Spanish and English.
 * Merged from posts.js. Mid CTA variant `mid`.
 */

export default [
  {
    slug: 'disponibilidad-inmediata-cv',
    date: '2026-08-22',
    category: 'career',
    related: ['checklist-cv-antes-de-enviar', 'enviar-cv-por-email', 'adaptar-cv-a-una-oferta'],
    es: {
      title: '¿Qué es disponibilidad inmediata en el CV? Qué poner y dónde',
      description:
        'Qué significa disponibilidad inmediata en un currículum, cuándo escribirla en el CV (y cuándo poner preaviso), dónde va en el PDF y cómo la lee un ATS.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: '“Disponibilidad inmediata” en el CV es una keyword que RR. HH. sí busca. También es una frase que, mal puesta, te hace parecer desesperado o mentiroso. En España significa: puedes empezar sin preaviso legal de 15 días (o el que marque tu contrato). No significa “contesto el teléfono a las 23 h” ni “acepto cualquier salario”. Si ya tienes un PDF, el trabajo es una línea, no un rediseño.',
        },
        {
          type: 'p',
          text: 'Esta guía cubre dónde colocarla, cuándo el preaviso es la respuesta correcta, y cómo el ATS lee el texto frente a un icono de calendario. Hermanas: [checklist antes de enviar](/blog/checklist-cv-antes-de-enviar) y [enviar el CV por email](/blog/enviar-cv-por-email). Si adaptas a una oferta que pide incorporación en septiembre, [adaptar el CV a una oferta](/blog/adaptar-cv-a-una-oferta). ClarCV (clarcv.com): subes el Word o PDF, dejas la frase en texto seleccionable, regeneras plantilla A4. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        {
          type: 'p',
          text: 'No inventes inmediata si tu convenio te ata 30 días. En la entrevista lo preguntan. Un “15 días de preaviso” honesto cierra más procesos que una keyword falsa.',
        },
        { type: 'h2', text: 'Qué significa (y qué no)' },
        {
          type: 'p',
          text: 'Inmediata = sin empleo actual, o con acuerdo de salida ya firmado, o autónomo que puede solapar. No es “híbrido tres días” ni “remoto desde el pueblo”. Modalidad y fecha de inicio son dos campos distintos. “Incorporación 1 de septiembre” no es inmediata; es una fecha. Escríbela si la oferta la pide.',
        },
        {
          type: 'ul',
          items: [
            'En desempleo o fin de contrato: “Disponibilidad inmediata” en cabecera o en el resumen.',
            'Con preaviso de 15 días: “Disponibilidad: 15 días de preaviso” — el ATS también pilla “preaviso”.',
            'Con preaviso de 1–3 meses (mandos): pon el plazo. “Inmediata” aquí es un filtro inverso.',
            'Estudiante o prácticas: “A partir de junio 2026” o “compatible con tardes”.',
            'Autónomo que busca contrato: “Disponibilidad a convenir / 2 semanas” suele ser más creíble.',
          ],
        },
        { type: 'h2', text: 'Dónde va en el archivo que ya tienes' },
        {
          type: 'p',
          text: 'Antes: un recuadro de Canva con un calendario y “YA”. El parser no extrae nada. Después: bajo el nombre, en texto: Madrid · Disponibilidad inmediata. O una frase del resumen: “CSM B2B, disponibilidad inmediata, inglés C1.” Tres palabras. No robes una viñeta de logro. No lo pongas como único H1 del PDF.',
        },
        {
          type: 'p',
          text: 'Si la oferta pide “incorporación inmediata” y tú la tienes, repite la frase una vez en el resumen. No hace falta un bloque “Otros datos”. El email de envío puede repetirlo en una línea del cuerpo: ver guía de envío. El asunto del correo no debe ser solo “Disponibilidad inmediata”: pon puesto + nombre.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El calendario de Canva no es una fecha',
          lead: 'Si tu disponibilidad es un pictograma, el ATS no lee “inmediata” ni “15 días”. Sube el PDF y deja la frase en texto.',
          from: 'Icono de calendario',
          to: 'Disponibilidad en una línea',
          button: 'Poner mi disponibilidad en texto',
        },
        { type: 'h2', text: 'Cuándo no ponerla' },
        {
          type: 'p',
          text: 'Si estás empleado y contento de negociar, el silencio es válido: la entrevista cubre la fecha. Si estás en periodo de prueba, dilo en la call, no en el PDF masivo. Si aplicas a empleo público con plazo de toma de posesión, el pliego manda, no tu slogan. Una línea de “inmediata” en un CV de director con preaviso de tres meses genera desconfianza, no velocidad.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿La pongo en el asunto del email?',
              a: 'Como apoyo, no como único asunto. Mejor: “Candidatura CSM — Ana López (disponibilidad inmediata)”.',
            },
            {
              q: '¿El ATS busca exactamente “disponibilidad inmediata”?',
              a: 'A veces. “Inmediata”, “incorporación inmediata” y “preaviso” también. En texto, no en un dibujo.',
            },
            {
              q: '¿Y si puedo empezar el 1 de septiembre?',
              a: 'Escribe la fecha. No es inmediata. Mentir aquí quema la oferta cuando RR. HH. planifica el onboarding.',
            },
            {
              q: '¿ClarCV calcula mi preaviso legal?',
              a: 'No. Tú escribes el plazo. La herramienta deja la frase en un PDF parseable.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'What does immediate availability mean on a CV? Where to put it',
      description:
        'What immediate availability means on a résumé, when to write it (and when to state notice period), where it goes on the PDF, and how ATS reads it.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: '“Immediate availability” is a phrase HR does search for. It is also a line that, badly placed, makes you look desperate or dishonest. In Spain it means you can start without the statutory 15-day notice (or whatever your contract says). It does not mean “I answer the phone at 11 p.m.” or “I will take any salary.” If you already have a PDF, this is one line, not a redesign.',
        },
        {
          type: 'p',
          text: 'This guide covers where to put it, when notice period is the right answer, and how ATS reads text versus a calendar icon. Siblings: [pre-send checklist](/blog/checklist-cv-antes-de-enviar) and [emailing your CV](/blog/enviar-cv-por-email). If the ad wants a September start, [tailor the CV to a job](/blog/adaptar-cv-a-una-oferta). ClarCV (clarcv.com): upload Word or PDF, keep the phrase as selectable text, regenerate an A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        {
          type: 'p',
          text: 'Do not invent “immediate” if your agreement ties you for 30 days. They will ask in interview. Honest “15 days notice” closes more processes than a fake keyword.',
        },
        { type: 'h2', text: 'What it means (and what it does not)' },
        {
          type: 'p',
          text: 'Immediate = not currently employed, or an exit already signed, or a freelancer who can overlap. It is not “hybrid three days” and not “remote from the village.” Work mode and start date are two different fields. “Start 1 September” is not immediate; it is a date. Write it if the ad asks.',
        },
        {
          type: 'ul',
          items: [
            'Unemployed or contract ending: “Immediate availability” in the header or summary.',
            '15-day notice: “Availability: 15 days notice” — ATS also catches “notice”.',
            '1–3 month notice (managers): put the lag. “Immediate” here is a reverse filter.',
            'Student or internship: “From June 2026” or “compatible with afternoons”.',
            'Freelancer seeking a contract: “Availability to agree / 2 weeks” is usually more believable.',
          ],
        },
        { type: 'h2', text: 'Where it goes on the file you already have' },
        {
          type: 'p',
          text: 'Before: a Canva box with a calendar and “NOW”. The parser extracts nothing. After: under the name, as text: Madrid · Immediate availability. Or one summary sentence: “B2B CSM, immediate availability, English C1.” Three words. Do not steal an achievement bullet. Do not make it the only H1 on the PDF.',
        },
        {
          type: 'p',
          text: 'If the ad asks for an immediate start and you have it, repeat the phrase once in the summary. You do not need an “Other” block. The send email can repeat it in one body line: see the emailing guide. The subject should not be only “Immediate availability”: use role + name.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'A Canva calendar is not a date',
          lead: 'If availability is a pictogram, ATS will not read “immediate” or “15 days”. Upload the PDF and leave the phrase as text.',
          from: 'Calendar icon',
          to: 'Availability in one line',
          button: 'Put my availability in text',
        },
        { type: 'h2', text: 'When to leave it off' },
        {
          type: 'p',
          text: 'If you are employed and happy to negotiate, silence is valid: the interview covers the date. If you are in a probation period, say it on the call, not on the mass PDF. Public-sector start dates follow the spec, not your slogan. “Immediate” on a director CV with three months notice creates distrust, not speed.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should it go in the email subject?',
              a: 'As support, not as the only subject. Better: “CSM application — Ana López (immediate availability)”.',
            },
            {
              q: 'Does ATS search for the exact phrase?',
              a: 'Sometimes. “Immediate”, “immediate start”, and “notice” also. In text, not in a drawing.',
            },
            {
              q: 'What if I can start 1 September?',
              a: 'Write the date. That is not immediate. Lying here burns the offer when HR plans onboarding.',
            },
            {
              q: 'Does ClarCV calculate my legal notice?',
              a: 'No. You write the lag. The tool leaves the phrase on a parseable PDF.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-firmado',
    date: '2026-08-22',
    category: 'format',
    related: ['que-no-poner-en-el-cv', 'checklist-cv-antes-de-enviar', 'enviar-cv-por-email'],
    es: {
      title: 'CV firmado en España: cuándo hace falta (y cuándo no)',
      description:
        '¿Hay que firmar el currículum? En empresa privada casi nunca. En administración, solo si el pliego lo pide. Firma-imagen y ATS. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'Un CV firmado en España es un mito de papelería, no un requisito de InfoJobs. En empresa privada, la firma manuscrita (o un PNG de tu rúbrica sobre el nombre) no aporta veracidad: el email y el proceso sí. En empleo público, oposiciones y algunos concursos, el pliego puede pedir firma o declaración responsable. Entonces firmas el documento que ellos nombran, no “por si acaso” todos tus PDFs.',
        },
        {
          type: 'p',
          text: 'Esta guía no es un tutorial de certificado FNMT. Cubre privado vs administración, por qué una imagen de firma rompe el parseo, y qué hacer con el archivo que ya tienes. Campos que sobran: [qué no poner en un CV](/blog/que-no-poner-en-el-cv). Envío: [email](/blog/enviar-cv-por-email). ClarCV regenera plantillas A4 sin rúbrica decorativa. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No firmamos por ti ni generamos un sello oficial.',
        },
        {
          type: 'p',
          text: 'Firmar un PDF de Canva “para que parezca serio” es el gesto inverso: el ATS tropieza con un gráfico encima del nombre y el humano no gana nada.',
        },
        { type: 'h2', text: 'Empresa privada: no firmes el CV' },
        {
          type: 'p',
          text: 'El contrato se firma después. El CV es un anuncio de hechos. Una rúbrica no demuestra que los datos sean ciertos; una mentira firmada sigue siendo mentira. LinkedIn, el email corporativo desde el que envías y las referencias (cuando las pidan) pesan más. No añadas “Declaro que los datos son veraces” al pie: ocupa una línea y suena a formulario de instituto.',
        },
        {
          type: 'ul',
          items: [
            'InfoJobs, LinkedIn Easy Apply, email a un reclutador: PDF sin firma.',
            'Agencia de colocación: el mismo archivo limpio; ellos ya tienen su hoja de cesión.',
            'Carta de presentación: tampoco se firma en 2026 salvo que el sobre sea papel (raro).',
            'Si un anuncio privado pide “CV firmado”, cumple esa oferta; no lo conviertas en tu plantilla eterna.',
          ],
        },
        { type: 'h2', text: 'Administración y oposiciones: el pliego manda' },
        {
          type: 'p',
          text: 'Ayuntamientos, comunidades, SEPE como canal de inscripción a ofertas públicas, universidades: lee la convocatoria. Si piden instancia + CV firmado, o sede electrónica con certificado, eso no es negociable. No sustituyas su PDF oficial por un ClarCV “moderno”. Lo desestiman por forma. Conserva el CV parseable para el mercado privado y el expediente para el proceso público.',
        },
        {
          type: 'p',
          text: 'Firma digital (FNMT, Cl@ve) va en la instancia o en el registro, no como PNG dentro de la columna de experiencia. Un sello de “firmado electrónicamente” incrustado como imagen es ruido para Workday y para el técnico que imprime.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'La rúbrica no es una keyword',
          lead: 'Si tu nombre está tapado por una firma-imagen, el ATS puede no extraer ni el contacto. Sube el PDF y deja el encabezado en texto.',
          from: 'PNG de firma sobre el nombre',
          to: 'Cabecera que se puede copiar',
          button: 'Quitar la firma del CV',
        },
        { type: 'h2', text: 'Por qué el parser odia la firma-imagen' },
        {
          type: 'p',
          text: 'Misma lógica que [iconos en el CV](/blog/iconos-en-el-cv): un gráfico no es tu apellido. Prueba del bloc de notas: selecciona el encabezado, pega. Si no sale el nombre y el email, esa rúbrica está cara. En ClarCV subes el archivo, ves fricción de estructura y regeneras una columna de texto. Tú decides si un proceso público exige otro PDF; la herramienta no lo inventa.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Una firma digital en el pie da más seriedad?',
              a: 'En privado, no. En público, usa el canal que indique la convocatoria (sede, certificado), no un adorno en el CV.',
            },
            {
              q: '¿Puedo firmar a mano, escanear y enviar?',
              a: 'Peor: suele crear un PDF-imagen. El ATS no lee el escaneo. Exporta digital y, si el pliego pide firma, firma el documento oficial.',
            },
            {
              q: '¿Y un “conforme” con fecha?',
              a: 'Innecesario en empresa. Si un organismo pide declaración responsable, usa su modelo.',
            },
            {
              q: '¿ClarCV añade una firma?',
              a: 'No. Genera plantillas A4 parseables. La firma, si aplica, es un trámite administrativo aparte.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'A signed CV in Spain: when you need it (and when not)',
      description:
        'Do you have to sign a résumé? Almost never in private hiring. In public jobs, only if the spec says so. Signature images and ATS. ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'A signed CV in Spain is stationery folklore, not an InfoJobs requirement. In private hiring, a handwritten signature (or a PNG of your flourish over the name) does not add truth: the email and the process do. In public employment, exams, and some tenders, the spec may ask for a signature or a responsible declaration. Then you sign the document they name, not “just in case” every PDF you own.',
        },
        {
          type: 'p',
          text: 'This is not an FNMT-certificate tutorial. It covers private vs public, why a signature image breaks parsing, and what to do with the file you already have. Extra fields: [what not to put on a CV](/blog/que-no-poner-en-el-cv). Sending: [email](/blog/enviar-cv-por-email). ClarCV regenerates A4 templates without a decorative flourish. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. We do not sign for you or generate an official stamp.',
        },
        {
          type: 'p',
          text: 'Signing a Canva PDF “to look serious” is the reverse gesture: ATS trips on a graphic over the name and the human gains nothing.',
        },
        { type: 'h2', text: 'Private companies: do not sign the CV' },
        {
          type: 'p',
          text: 'The contract is signed later. The CV is a statement of facts. A flourish does not prove the data is true; a signed lie is still a lie. LinkedIn, the mailbox you send from, and references (when they ask) weigh more. Do not add “I declare these data are accurate” at the foot: it costs a line and sounds like a school form.',
        },
        {
          type: 'ul',
          items: [
            'InfoJobs, LinkedIn Easy Apply, email to a recruiter: PDF with no signature.',
            'Staffing agency: the same clean file; they already have their disclosure sheet.',
            'Cover letter: also unsigned in 2026 unless you are mailing paper (rare).',
            'If one private ad asks for a “signed CV”, comply for that job; do not make it your forever template.',
          ],
        },
        { type: 'h2', text: 'Public sector and exams: the spec wins' },
        {
          type: 'p',
          text: 'City halls, regions, SEPE as a channel into public vacancies, universities: read the call. If they want an application form + signed CV, or an e-office with a certificate, that is not negotiable. Do not replace their official PDF with a “modern” ClarCV. They reject on form. Keep the parseable CV for the private market and the dossier for the public process.',
        },
        {
          type: 'p',
          text: 'A digital signature (FNMT, Cl@ve) belongs on the application or the registry, not as a PNG inside the experience column. An “electronically signed” badge embedded as an image is noise for Workday and for the person who prints.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The flourish is not a keyword',
          lead: 'If your name is covered by a signature image, ATS may fail to extract even contact. Upload the PDF and leave the header as text.',
          from: 'Signature PNG over the name',
          to: 'A header you can copy',
          button: 'Remove the signature from my CV',
        },
        { type: 'h2', text: 'Why parsers hate signature images' },
        {
          type: 'p',
          text: 'Same logic as [icons on the CV](/blog/iconos-en-el-cv): a graphic is not your surname. Notepad test: select the header, paste. If name and email do not appear, that flourish is expensive. In ClarCV you upload the file, see structure friction, and regenerate a text column. You decide if a public process needs another PDF; the tool does not invent it.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does a digital signature in the footer look more serious?',
              a: 'In private hiring, no. In public hiring, use the channel the call names (e-office, certificate), not decoration on the CV.',
            },
            {
              q: 'Can I sign by hand, scan, and send?',
              a: 'Worse: it often creates an image-only PDF. ATS does not read the scan. Export digitally and, if the spec wants a signature, sign the official document.',
            },
            {
              q: 'What about “agreed” plus a date?',
              a: 'Unnecessary in companies. If a body wants a responsible declaration, use their form.',
            },
            {
              q: 'Does ClarCV add a signature?',
              a: 'No. It generates parseable A4 templates. A signature, if required, is a separate admin step.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'color-y-fuente-cv',
    date: '2026-08-22',
    category: 'format',
    related: ['plantillas-cv-compatibles-ats', 'iconos-en-el-cv', 'cv-canva-y-ats'],
    es: {
      title: 'Color y fuente en el CV: qué lee el ATS en 2026',
      description:
        'Calibri, Arial o Georgia en negro; un acento, no un degradado. Qué tipografías y colores rompen el filtro y cómo arreglar el PDF. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Color y fuente en el CV no son “marca personal”. Son contraste, parseo y una impresión en blanco y negro en RR. HH. El ATS extrae caracteres, no tu paleta de Canva. Un título en script, un gris claro sobre blanco o un degradado detrás del nombre pueden dejar el encabezado ilegible para el filtro y para quien imprime. Si ya tienes un PDF bonito que no se selecciona bien, el arreglo es tipografía del sistema y un acento, no más diseño.',
        },
        {
          type: 'p',
          text: 'Puente a plantillas: [plantillas compatibles con ATS](/blog/plantillas-cv-compatibles-ats). El primo de los pictogramas: [iconos en el CV](/blog/iconos-en-el-cv). Si el archivo nació en Canva: [Canva y ATS](/blog/cv-canva-y-ats). ClarCV aplica plantillas A4 con fuentes embebidas y jerarquía simple. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        {
          type: 'p',
          text: 'Un acento en el nombre o en los H2 es suficiente. El cuerpo en negro. Si el valor del CV depende de un color pantone, el documento es frágil.',
        },
        { type: 'h2', text: 'Fuentes que sobreviven al filtro y a la impresora' },
        {
          type: 'p',
          text: 'Usa una sans para títulos y cuerpo, o una serif sobria en el nombre. Calibri, Arial, Helvetica, Georgia, Source Sans, Liberation: el parser y Word las conocen. Evita script, “handwriting”, Comic Sans, y las display de moda que se sustituyen por Arial al abrir en otro PC. Tamaño: nombre 16–20 pt, títulos de sección 12–13, cuerpo 10–11. Menos de 9 pt es un infierno en móvil y en OCR.',
        },
        {
          type: 'ul',
          items: [
            'Una familia, dos pesos (regular + semibold). No mezcles cinco Google Fonts.',
            'Evita ligaduras raras y “small caps” en el email: C++ y .NET se rompen.',
            'No pongas el teléfono en un icono de fuente dingbat: el ATS no marca.',
            'Si exportas PDF, incrusta fuentes. Un PDF que pide descargar una font es un riesgo.',
          ],
        },
        { type: 'h2', text: 'Color: un acento, texto negro, contraste real' },
        {
          type: 'p',
          text: 'Antes: degradado morado, texto blanco sobre foto, líneas coral. Después: texto #111 o negro, un azul o verde oscuro en los H2, línea fina. Imprime en escala de grises: si el H2 desaparece, el acento era demasiado claro. El reclutador de una ETT a menudo imprime. El ATS no “ve” el brand color; si el texto es blanco sobre recuadro, a veces extrae vacío.',
        },
        {
          type: 'p',
          text: 'Barras de “Photoshop 80%” en color no son un nivel: son un gráfico. Quien quiera skill en texto, que lea la guía de iconos. El color de la plantilla ClarCV está pensado para A4 y para no pelearse con el parser. Tú eliges la plantilla; no persigas un pantone de marca en el CV de empleo.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El degradado no es una sección',
          lead: 'Si el nombre va en blanco sobre un banner, el filtro puede no extraerlo. Sube el PDF y regenera tipografía y contraste de CV.',
          from: 'Canva con degradado',
          to: 'Fuente de sistema + acento',
          button: 'Cambiar fuente y color',
        },
        { type: 'h2', text: 'Cómo arreglar el archivo que ya tienes' },
        {
          type: 'p',
          text: 'Abre el PDF, selecciona un párrafo. Si el orden es absurdo o salen caracteres huecos, la fuente o las cajas están rotas. En ClarCV subes el Word o PDF, ves claridad y estructura, aplicas una plantilla con encabezados estándar. No prometemos un porcentaje contra Workday. Sí un A4 que se puede copiar. Empieza gratis; Pro si iteras plantillas a menudo.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Puedo usar la fuente de mi marca personal?',
              a: 'En el portfolio, sí. En el PDF del portal, una fuente de sistema. El ATS y la ETT no instalan tu font.',
            },
            {
              q: '¿El CV en negro sobre blanco es “aburrido”?',
              a: 'Es legible. El diseño se gana con jerarquía y logros, no con un coral de moda.',
            },
            {
              q: '¿Color en creativos (diseño, marketing)?',
              a: 'Un acento, sí. Un infográfico, no para InfoJobs. El portfolio lleva el color; el CV lleva el texto.',
            },
            {
              q: '¿ClarCV deja elegir cualquier tipografía?',
              a: 'Las plantillas usan familias seguras para PDF/ATS. No es un editor de Canva.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV fonts and colors: what ATS still reads in 2026',
      description:
        'Calibri, Arial or Georgia in black; one accent, not a gradient. Which typefaces and colors break the filter, and how to fix the PDF. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Color and type on a CV are not “personal brand.” They are contrast, parsing, and a black-and-white printout in HR. ATS extracts characters, not your Canva palette. A script heading, light grey on white, or a gradient behind the name can leave the header unreadable for the filter and for whoever prints. If you already have a pretty PDF that does not select well, the fix is a system font and one accent, not more design.',
        },
        {
          type: 'p',
          text: 'Bridge to templates: [ATS-friendly templates](/blog/plantillas-cv-compatibles-ats). Cousin of pictograms: [icons on the CV](/blog/iconos-en-el-cv). If the file was born in Canva: [Canva and ATS](/blog/cv-canva-y-ats). ClarCV applies A4 templates with embedded fonts and simple hierarchy. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        {
          type: 'p',
          text: 'An accent on the name or H2s is enough. Body in black. If the CV’s value depends on a Pantone, the document is fragile.',
        },
        { type: 'h2', text: 'Fonts that survive the filter and the printer' },
        {
          type: 'p',
          text: 'Use a sans for headings and body, or a sober serif for the name. Calibri, Arial, Helvetica, Georgia, Source Sans, Liberation: parsers and Word know them. Avoid script, “handwriting”, Comic Sans, and trendy display faces that become Arial on another PC. Size: name 16–20 pt, section titles 12–13, body 10–11. Under 9 pt is hell on mobile and in OCR.',
        },
        {
          type: 'ul',
          items: [
            'One family, two weights (regular + semibold). Do not mix five Google Fonts.',
            'Avoid odd ligatures and small caps in the email: C++ and .NET break.',
            'Do not put the phone in a dingbat icon: ATS will not dial it.',
            'If you export PDF, embed fonts. A PDF that asks to download a font is a risk.',
          ],
        },
        { type: 'h2', text: 'Color: one accent, black text, real contrast' },
        {
          type: 'p',
          text: 'Before: purple gradient, white text on a photo, coral rules. After: #111 or black text, a dark blue or green on H2s, a thin line. Print greyscale: if the H2 vanishes, the accent was too light. A staffing recruiter often prints. ATS does not “see” brand color; if type is white on a box, it sometimes extracts emptiness.',
        },
        {
          type: 'p',
          text: 'Colored “Photoshop 80%” bars are not a level: they are a graphic. Skills as text: see the icons guide. ClarCV template color is built for A4 and not to fight the parser. You pick the template; do not chase a brand Pantone on the job-application CV.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'A gradient is not a section',
          lead: 'If the name is white on a banner, the filter may not extract it. Upload the PDF and regenerate CV type and contrast.',
          from: 'Canva with a gradient',
          to: 'System font + accent',
          button: 'Change font and color',
        },
        { type: 'h2', text: 'How to fix the file you already have' },
        {
          type: 'p',
          text: 'Open the PDF, select a paragraph. If the order is nonsense or hollow characters appear, the font or the boxes are broken. In ClarCV you upload Word or PDF, see clarity and structure, apply a template with standard headings. We do not promise a percentage against Workday. We do promise an A4 you can copy. Start free; Pro if you iterate templates often.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Can I use my personal-brand typeface?',
              a: 'On the portfolio, yes. On the board PDF, a system font. ATS and the agency will not install your font.',
            },
            {
              q: 'Is black-on-white “boring”?',
              a: 'It is readable. Design is won with hierarchy and achievements, not this year’s coral.',
            },
            {
              q: 'Color for creatives (design, marketing)?',
              a: 'One accent, yes. An infographic, not for InfoJobs. The portfolio carries color; the CV carries text.',
            },
            {
              q: 'Does ClarCV let me pick any typeface?',
              a: 'Templates use PDF/ATS-safe families. It is not a Canva editor.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'linkedin-url-en-el-cv',
    date: '2026-08-22',
    category: 'career',
    related: ['linkedin-y-cv', 'checklist-cv-antes-de-enviar', 'que-no-poner-en-el-cv'],
    es: {
      title: 'URL de LinkedIn en el CV: custom, texto y sin QR',
      description:
        'Cómo poner el enlace de LinkedIn en el currículum: URL personalizada, en texto, sin QR ni bit.ly. No es alinear el perfil (eso es otra guía). ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'La URL de LinkedIn en el CV es un dato de contacto, no un ensayo. El reclutador la pega en el navegador o el ATS la extrae si está en texto. Un icono azul, un QR o linkedin.com/in/ana-lopez-9381b02a fallan de formas distintas: el parser no sigue el pictograma, el móvil de RR. HH. no escanea el QR del PDF, y el slug con números parece un perfil abandonado. Esta guía es solo el enlace. Alinear títulos y fechas con el perfil es otra pieza: [LinkedIn y CV](/blog/linkedin-y-cv).',
        },
        {
          type: 'p',
          text: 'Hermanas de limpieza: [checklist](/blog/checklist-cv-antes-de-enviar) y [qué no poner](/blog/que-no-poner-en-el-cv). ClarCV deja el LinkedIn en la cabecera como texto. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No editamos tu perfil de LinkedIn.',
        },
        {
          type: 'p',
          text: 'No pegas el About entero en el PDF. Pegas una URL corta que abre el perfil correcto.',
        },
        { type: 'h2', text: 'Personaliza el slug (cinco minutos)' },
        {
          type: 'p',
          text: 'En LinkedIn: Identificar → URL pública → Editar. linkedin.com/in/ana-lopez-pm o /in/analopez. Minúsculas, guiones, sin tildes si se rompen. Evita el hash aleatorio de cuando te registraste. Si tu nombre está cogido, añade el rol o la ciudad: /in/ana-lopez-madrid. Esa es la cadena que va al CV, no la URL completa con ?trk= de una notificación.',
        },
        {
          type: 'ul',
          items: [
            'En el PDF: linkedin.com/in/tu-slug (se puede omitir https://).',
            'Hipervínculo en el mismo texto. No “haz clic aquí”.',
            'Nada de bit.ly, TinyURL ni QR: el ATS no los resuelve igual y parecen tracking.',
            'Nada de linkedin.com/feed ni de la URL de una publicación.',
            'Si tienes empresa en LinkedIn, el perfil personal es el que va al CV de candidato.',
          ],
        },
        { type: 'h2', text: 'Dónde va (línea de contacto, no un recuadro)' },
        {
          type: 'p',
          text: 'Antes: un botón circular de LinkedIn en el margen, sin la palabra. Después: Email · Teléfono · linkedin.com/in/ana-lopez · Madrid. El parser busca “linkedin”; el humano copia. No uses una columna de iconos. No pongas tres redes si no eres community: Instagram personal sobra. Portfolio o GitHub sí, en la misma línea, también en texto. Guía tech: [proyectos y GitHub](/blog/proyectos-personales-github-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El icono azul no es una URL',
          lead: 'Si LinkedIn es un pictograma, el ATS no extrae tu perfil. Sube el PDF y deja linkedin.com/in/… en texto.',
          from: 'Botón de LinkedIn',
          to: 'Slug en la cabecera',
          button: 'Poner mi URL en el CV',
        },
        { type: 'h2', text: 'Qué no copiar del perfil al PDF' },
        {
          type: 'p',
          text: 'El CV no es un dump: ni las 20 recomendaciones, ni el banner, ni “open to work” como titular único. El titular de LinkedIn puede ser más largo; el del PDF, el puesto objetivo. Fechas y empresas deben coincidir —eso sí va en la guía de alineación—. Aquí el fallo típico es un QR a un perfil desactualizado o un enlace a /in/nombre-12345 que ya no usas.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo www o https?',
              a: 'Da igual si el hipervínculo funciona. linkedin.com/in/slug es suficiente y más limpio.',
            },
            {
              q: '¿Y si el perfil está en inglés y el CV en español?',
              a: 'El enlace es el mismo. El idioma del documento sigue la oferta: [CV en inglés o en español](/blog/cv-ingles-o-espanol).',
            },
            {
              q: '¿Puedo poner solo “LinkedIn” como palabra clicable?',
              a: 'Mejor la URL visible. Algunos parsers y algunos humanos imprimen en papel y pierden el link.',
            },
            {
              q: '¿ClarCV crea el slug de LinkedIn?',
              a: 'No. Lo personalizas en LinkedIn y lo pegas. Nosotros lo dejamos como texto en la plantilla.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'LinkedIn URL on your CV: custom, text, no QR',
      description:
        'How to put a LinkedIn link on a résumé: custom URL, as text, no QR or bit.ly. This is not profile alignment (that is another guide). ClarCV.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'The LinkedIn URL on a CV is a contact field, not an essay. The recruiter pastes it in a browser, or ATS extracts it if it is text. A blue icon, a QR, or linkedin.com/in/ana-lopez-9381b02a fail in different ways: the parser does not follow the pictogram, HR’s phone does not scan a QR on a PDF, and a numbered slug looks like an abandoned profile. This guide is only the link. Aligning titles and dates with the profile is a different piece: [LinkedIn and CV](/blog/linkedin-y-cv).',
        },
        {
          type: 'p',
          text: 'Cleanup siblings: [checklist](/blog/checklist-cv-antes-de-enviar) and [what not to include](/blog/que-no-poner-en-el-cv). ClarCV leaves LinkedIn in the header as text. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. We do not edit your LinkedIn profile.',
        },
        {
          type: 'p',
          text: 'You do not paste the whole About into the PDF. You paste a short URL that opens the right profile.',
        },
        { type: 'h2', text: 'Customise the slug (five minutes)' },
        {
          type: 'p',
          text: 'On LinkedIn: Identity → public URL → Edit. linkedin.com/in/ana-lopez-pm or /in/analopez. Lowercase, hyphens, no accents if they break. Drop the random hash from when you signed up. If your name is taken, add the role or city: /in/ana-lopez-madrid. That is the string that goes on the CV, not the full URL with ?trk= from a notification.',
        },
        {
          type: 'ul',
          items: [
            'On the PDF: linkedin.com/in/your-slug (you can omit https://).',
            'Hyperlink the same text. Not “click here”.',
            'No bit.ly, TinyURL, or QR: ATS does not resolve them the same way and they look like tracking.',
            'No linkedin.com/feed and no URL of a post.',
            'If you have a company Page, the personal profile is what goes on a candidate CV.',
          ],
        },
        { type: 'h2', text: 'Where it goes (contact line, not a box)' },
        {
          type: 'p',
          text: 'Before: a circular LinkedIn button in the margin, without the word. After: Email · Phone · linkedin.com/in/ana-lopez · Madrid. The parser looks for “linkedin”; the human copies. Do not use an icon column. Do not list three networks if you are not community: personal Instagram is surplus. Portfolio or GitHub yes, on the same line, also as text. Tech guide: [projects and GitHub](/blog/proyectos-personales-github-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The blue icon is not a URL',
          lead: 'If LinkedIn is a pictogram, ATS will not extract your profile. Upload the PDF and leave linkedin.com/in/… as text.',
          from: 'LinkedIn button',
          to: 'Slug in the header',
          button: 'Put my URL on the CV',
        },
        { type: 'h2', text: 'What not to copy from the profile onto the PDF' },
        {
          type: 'p',
          text: 'The CV is not a dump: not the 20 recommendations, not the banner, not “open to work” as the only headline. The LinkedIn headline can be longer; the PDF headline is the target job. Dates and employers must match — that belongs in the alignment guide. The typical fail here is a QR to a stale profile or a link to /in/name-12345 you no longer use.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I put www or https?',
              a: 'It does not matter if the hyperlink works. linkedin.com/in/slug is enough and cleaner.',
            },
            {
              q: 'What if the profile is in English and the CV in Spanish?',
              a: 'The link is the same. Document language follows the ad: [CV in English or Spanish](/blog/cv-ingles-o-espanol).',
            },
            {
              q: 'Can I use only the word “LinkedIn” as the clickable bit?',
              a: 'Better a visible URL. Some parsers and some humans print on paper and lose the link.',
            },
            {
              q: 'Does ClarCV create the LinkedIn slug?',
              a: 'No. You customise it on LinkedIn and paste it. We leave it as text on the template.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'proyectos-personales-github-cv',
    date: '2026-08-22',
    category: 'career',
    related: ['cv-para-tecnologia', 'primer-empleo-cv', 'cv-sin-experiencia'],
    es: {
      title: 'Proyectos personales y GitHub en el CV: cuáles cuentan',
      description:
        'Qué side projects poner en el currículum, cómo escribirlos y qué URL de GitHub pegar. Tutoriales clonados no suman. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Proyectos personales en el CV cuentan cuando hay un problema, un rol tuyo y una prueba (repo, demo, usuarios). Un tutorial de YouTube clonado, un README vacío o “varios proyectos en GitHub” no. El hiring manager tech abre el enlace en el screening. Si el repo es un fork sin commits, has gastado una viñeta. Esta guía reescribe la sección que ya tienes, no te pide un portfolio de cero. Marco de stack: [CV para tecnología](/blog/cv-para-tecnologia). Si aún no hay contrato: [primer empleo](/blog/primer-empleo-cv) y [sin experiencia](/blog/cv-sin-experiencia).',
        },
        {
          type: 'p',
          text: 'ClarCV deja GitHub y las viñetas en texto parseable. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No analizamos tu código ni inventamos stars.',
        },
        {
          type: 'p',
          text: 'Un proyecto vivo con 40 usuarios reales gana a cinco clones de Netflix. Calidad, no cantidad.',
        },
        { type: 'h2', text: 'Qué proyecto sobrevive a un screening' },
        {
          type: 'p',
          text: 'Antes: “Proyectos: app de tareas (React).” Después: “Habit tracker (2025): React + FastAPI, 3 colaboradores, 80 usuarios beta, despliegue en Fly.io — github.com/ana/habits.” Misma persona. Solo la segunda tiene stack, alcance y URL. Si es diseño o datos, cambia el artefacto: Figma + caso, notebook + dataset, no un repo vacío.',
        },
        {
          type: 'ul',
          items: [
            'Nombre, año, tu rol (autora, no “el equipo”).',
            'Stack en palabras que la oferta usa (Django, dbt, Figma), no un logo.',
            'Resultado: usuarios, PR merged, tiempo ahorrado, dataset publicado.',
            'Enlace: repo concreto o demo, no solo github.com/tu-usuario si el perfil es un cajón.',
            'Confidencial de cliente: describe el problema sin el nombre si el NDA lo impide.',
          ],
        },
        { type: 'h2', text: 'GitHub: perfil vs repositorio' },
        {
          type: 'p',
          text: 'El perfil en contacto (github.com/ana) vale si está curado: pinned repos, README de perfil honesto. Si tienes 40 forks de cursos, no pegues el perfil: pega dos repos. El ATS busca “GitHub” y a veces la URL; el humano clica. Un QR no. Commits de madrugada no son un logro; un CONTRIBUTING.md y issues cerradas sí cuentan en junior. Guía de URL en cabecera: [LinkedIn en el CV](/blog/linkedin-url-en-el-cv) — misma lógica, otro dominio.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El logo del pulpo no es un repo',
          lead: 'Si GitHub es un icono, el filtro no extrae tus proyectos. Sube el PDF y deja repos y stack en texto.',
          from: 'Icono de GitHub',
          to: 'Repo + stack en viñetas',
          button: 'Pasar mis proyectos a texto',
        },
        { type: 'h2', text: 'Qué quitar del archivo que ya tienes' },
        {
          type: 'p',
          text: 'Bootcamps: un proyecto final bien descrito, no los ocho katas. Cursos Udemy: [cuáles cuentan](/blog/cursos-online-en-el-cv). Senior con empleo reciente: máximo dos side projects; el resto diluye el cargo. En ClarCV recortas a una columna A4. Tú verificas que el enlace abre; el modelo no debe inventar un GitHub.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Los proyectos van en Experiencia?',
              a: 'Si fueron el trabajo (freelance, producto propio con ingresos), sí. Si son side, sección Proyectos. No los disfraces de empleo asalariado.',
            },
            {
              q: '¿Pongo todos los repos pinned?',
              a: 'Dos o tres en el CV. El perfil puede tener más.',
            },
            {
              q: '¿Un proyecto sin código (no-code, Notion)?',
              a: 'Sí si hay usuarios o un caso. Describe la herramienta y el resultado. No finjas un stack que no usaste.',
            },
            {
              q: '¿ClarCV lee GitHub?',
              a: 'No. Formatea el PDF. Los enlaces y las cifras los pones tú.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Personal projects and GitHub on a CV: which ones count',
      description:
        'Which side projects to list, how to write them, and which GitHub URL to paste. Cloned tutorials do not help. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Personal projects on a CV count when there is a problem, your role, and proof (repo, demo, users). A cloned YouTube tutorial, an empty README, or “various GitHub projects” do not. The tech hiring manager opens the link in screening. If the repo is a fork with no commits, you have spent a bullet. This guide rewrites the section you already have; it does not ask for a portfolio from scratch. Stack frame: [CV for tech](/blog/cv-para-tecnologia). If there is no contract yet: [first job](/blog/primer-empleo-cv) and [no experience](/blog/cv-sin-experiencia).',
        },
        {
          type: 'p',
          text: 'ClarCV leaves GitHub and the bullets as parseable text. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. We do not analyse your code or invent stars.',
        },
        {
          type: 'p',
          text: 'One live project with 40 real users beats five Netflix clones. Quality, not quantity.',
        },
        { type: 'h2', text: 'Which project survives a screen' },
        {
          type: 'p',
          text: 'Before: “Projects: todo app (React).” After: “Habit tracker (2025): React + FastAPI, 3 collaborators, 80 beta users, deploy on Fly.io — github.com/ana/habits.” Same person. Only the second has stack, scope, and a URL. If you are design or data, change the artefact: Figma + case, notebook + dataset, not an empty repo.',
        },
        {
          type: 'ul',
          items: [
            'Name, year, your role (author, not “the team”).',
            'Stack in words the ad uses (Django, dbt, Figma), not a logo.',
            'Outcome: users, PRs merged, time saved, dataset published.',
            'Link: a specific repo or demo, not only github.com/you if the profile is a junk drawer.',
            'Client confidentiality: describe the problem without the name if the NDA blocks it.',
          ],
        },
        { type: 'h2', text: 'GitHub: profile vs repository' },
        {
          type: 'p',
          text: 'The profile in contact (github.com/ana) works if it is curated: pinned repos, an honest profile README. If you have 40 course forks, do not paste the profile: paste two repos. ATS looks for “GitHub” and sometimes the URL; the human clicks. No QR. Late-night commits are not an achievement; a CONTRIBUTING.md and closed issues do count for juniors. Header-URL logic: [LinkedIn on the CV](/blog/linkedin-url-en-el-cv) — same idea, different domain.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The octopus logo is not a repo',
          lead: 'If GitHub is an icon, the filter will not extract your projects. Upload the PDF and leave repos and stack as text.',
          from: 'GitHub icon',
          to: 'Repo + stack in bullets',
          button: 'Turn my projects into text',
        },
        { type: 'h2', text: 'What to cut from the file you already have' },
        {
          type: 'p',
          text: 'Bootcamps: one well-described capstone, not eight katas. Udemy courses: [which count](/blog/cursos-online-en-el-cv). Seniors with recent employment: two side projects max; the rest dilutes the job. In ClarCV you trim to one A4 column. You check that the link opens; the model must not invent a GitHub.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Do projects go under Experience?',
              a: 'If they were the job (freelance, own product with revenue), yes. If they are side work, a Projects section. Do not disguise them as salaried roles.',
            },
            {
              q: 'Should I list every pinned repo?',
              a: 'Two or three on the CV. The profile can hold more.',
            },
            {
              q: 'A project with no code (no-code, Notion)?',
              a: 'Yes if there are users or a case. Name the tool and the outcome. Do not fake a stack you did not use.',
            },
            {
              q: 'Does ClarCV read GitHub?',
              a: 'No. It formats the PDF. You supply the links and the numbers.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'tfg-tfm-en-el-cv',
    date: '2026-08-22',
    category: 'career',
    related: ['primer-empleo-cv', 'cv-para-practicas', 'cv-sin-experiencia'],
    es: {
      title: 'TFG y TFM en el CV: cómo poner el trabajo fin de estudios',
      description:
        'Cómo incluir el TFG o TFM en el currículum: formación vs proyectos, nota, viñetas que el ATS lee y qué no copiar de la memoria académica.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'El TFG o el TFM en el CV es una prueba de proyecto, no un capítulo de la memoria. Quien sale del grado o entra a prácticas tiene poco más: el trabajo fin de estudios, bien descrito, diferencia. Quien ya tiene dos empleos: una línea bajo el título, o nada si no aporta al puesto. No pegues el resumen de 800 palabras ni la nota del acta si es un 5,0. Esta guía encaja el bloque en el archivo que ya tienes. Contexto junior: [primer empleo](/blog/primer-empleo-cv), [prácticas](/blog/cv-para-practicas), [sin experiencia](/blog/cv-sin-experiencia).',
        },
        {
          type: 'p',
          text: 'ClarCV no redacta tu TFG. Deja título, universidad y viñetas en una plantilla A4. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        {
          type: 'p',
          text: 'El tribunal ya leyó la memoria. RR. HH. quiere problema, método, resultado y, si aplica, stack o sector.',
        },
        { type: 'h2', text: 'Formación o Proyectos (elige uno)' },
        {
          type: 'p',
          text: 'Si el TFG es lo más cercano a un caso real, ponlo en Proyectos con el contexto “TFG, Universidad X, 2025”. Si es un requisito más del título, déjalo en Formación: Grado en… — TFG: “Optimización de rutas (Python, 40 páginas, notable).” Duplicarlo en las dos secciones es ruido. El TFM profesionalizante (empresa) se describe como proyecto con el nombre de la compañía si te dejan; si hay NDA, el sector y el entregable.',
        },
        {
          type: 'ul',
          items: [
            'Título del trabajo en una línea (el real, no un SEO inventado).',
            'Año, centro, director solo si es un nombre que abre puertas en ese sector.',
            '2–3 viñetas: objetivo, herramientas, resultado (prototipo, paper, ahorro simulado).',
            'Nota: sí si es notable o matrícula y el puesto es analítico/académico; no si es un aprobado justo.',
            'Enlace al PDF en el repositorio de la universidad si es público; no subas la memoria entera al portal.',
          ],
        },
        { type: 'h2', text: 'Antes y después en el PDF' },
        {
          type: 'p',
          text: 'Antes: “TFG sobre marketing.” Después: “TFG (UCM, 2025): análisis de churn en telco de 12k clientes (SQL + Python); propuesta de playbook de retención presentada a tutora de empresa.” El ATS pilla Python, SQL, churn. El humano entiende el alcance. Si el trabajo es Fine Arts, el entregable (exposición, catálogo) sustituye al stack. Relación con side projects: [GitHub y proyectos](/blog/proyectos-personales-github-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'La memoria de 80 páginas no es una viñeta',
          lead: 'Si el TFG es un muro de texto o un PDF escaneado, el filtro no extrae el stack. Sube el CV y deja título y herramientas en texto.',
          from: 'Resumen académico largo',
          to: 'TFG en 3 viñetas',
          button: 'Encajar mi TFG en el CV',
        },
        { type: 'h2', text: 'Qué no poner' },
        {
          type: 'p',
          text: 'Ni el DNI del acta, ni la rúbrica del tribunal, ni “sobresaliente por unanimidad” si no lo fue. Ni el embargo de una empresa como si fuera un paper público. Para un [máster](/blog/cv-para-master) de admisión, a veces piden la memoria aparte: el CV sigue siendo corto. En ClarCV recortas. Tú no hinchas la nota; el modelo no debe inventar un 9,8.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿El TFG cuenta como experiencia laboral?',
              a: 'No como empleo. Sí como proyecto académico. No lo fechas como si cobrases nómina.',
            },
            {
              q: '¿Pongo el TFG si me gradué hace diez años?',
              a: 'Solo si el tema es el puesto (tesis que sigue siendo tu especialidad). Si no, el título basta.',
            },
            {
              q: '¿Y un trabajo fin de ciclo (FP)?',
              a: 'Misma receta: proyecto + herramientas + resultado. En España es una keyword útil en ofertas de técnico.',
            },
            {
              q: '¿ClarCV lee mi memoria?',
              a: 'No. Formatea el CV. El resumen del TFG lo escribes tú.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Undergraduate and master’s theses on a CV: how to list them',
      description:
        'How to include your bachelor’s or master’s thesis on a résumé: education vs projects, grade, ATS-friendly bullets, and what not to copy from the academic PDF.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'A bachelor’s or master’s thesis on a CV is project proof, not a chapter of the dissertation. If you are leaving the degree or entering internships you have little else: a well-described capstone differentiates. If you already have two jobs: one line under the degree, or nothing if it does not help the role. Do not paste an 800-word abstract or a 5.0/10 grade. This guide fits the block into the file you already have. Junior context: [first job](/blog/primer-empleo-cv), [internships](/blog/cv-para-practicas), [no experience](/blog/cv-sin-experiencia).',
        },
        {
          type: 'p',
          text: 'ClarCV does not write your thesis. It leaves title, university, and bullets on an A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        {
          type: 'p',
          text: 'The committee already read the dissertation. HR wants problem, method, result, and stack or sector if they apply.',
        },
        { type: 'h2', text: 'Education or Projects (pick one)' },
        {
          type: 'p',
          text: 'If the thesis is the closest thing to a real case, put it under Projects with the context “BSc thesis, University X, 2025”. If it is just a degree requirement, leave it under Education: BSc in… — Thesis: “Route optimisation (Python, 40 pages, notable).” Duplicating it in both sections is noise. A practice-based master’s (company) is described as a project with the company name if they allow it; if there is an NDA, the sector and the deliverable.',
        },
        {
          type: 'ul',
          items: [
            'Thesis title in one line (the real one, not invented SEO).',
            'Year, school, supervisor only if the name opens doors in that sector.',
            '2–3 bullets: goal, tools, outcome (prototype, paper, simulated saving).',
            'Grade: yes if it is high and the role is analytical/academic; no if it is a bare pass.',
            'Link to the public university repository if it exists; do not upload the full dissertation to the job board.',
          ],
        },
        { type: 'h2', text: 'Before and after on the PDF' },
        {
          type: 'p',
          text: 'Before: “Thesis on marketing.” After: “BSc thesis (UCM, 2025): churn analysis for a 12k-customer telco (SQL + Python); retention playbook presented to a company supervisor.” ATS catches Python, SQL, churn. The human understands scope. If the work is Fine Arts, the deliverable (show, catalogue) replaces the stack. Relation to side projects: [GitHub and projects](/blog/proyectos-personales-github-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'An 80-page dissertation is not a bullet',
          lead: 'If the thesis is a wall of text or a scanned PDF, the filter will not extract the stack. Upload the CV and leave title and tools as text.',
          from: 'Long academic abstract',
          to: 'Thesis in 3 bullets',
          button: 'Fit my thesis on the CV',
        },
        { type: 'h2', text: 'What to leave off' },
        {
          type: 'p',
          text: 'Not the exam-board ID, not the committee signatures, not “outstanding by unanimous vote” if it was not. Not an embargoed company project as if it were a public paper. For a [master’s application](/blog/cv-para-master) they sometimes want the dissertation separately: the CV stays short. In ClarCV you trim. You do not inflate the grade; the model must not invent a 9.8.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does a thesis count as work experience?',
              a: 'Not as a job. Yes as an academic project. Do not date it as if you were on payroll.',
            },
            {
              q: 'Should I list a thesis from ten years ago?',
              a: 'Only if the topic is the job (a dissertation that is still your specialty). Otherwise the degree title is enough.',
            },
            {
              q: 'What about a vocational capstone (FP in Spain)?',
              a: 'Same recipe: project + tools + outcome. In Spain it is a useful keyword on technician ads.',
            },
            {
              q: 'Does ClarCV read my dissertation?',
              a: 'No. It formats the CV. You write the thesis summary.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
